import {useTranslation} from 'react-i18next';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {Box, Menu, Typography, useTheme} from '@mui/material';

import {EmpionActionMenuItems} from '../../../../../../components/EmpionActionMenuItems/EmpionActionMenuItems';
import {EmpionButton} from '../../../../../../components/EmpionButton/EmpionButton';
import {empionGreen, empionOrange} from '../../../../../../layouts/theme/EmpionColors';
import {getMenuStyles} from '../../../../../../layouts/theme/ThemeHelpers';
import {MatchStatusTab} from '../../../../../../types/shared-recruiting-types';
import {snakeToCamel} from '../../../../../../utils/matchUtils';

interface Props {
  status: string;
  menuItems: any[];
  handleClick: (event: React.MouseEvent<HTMLElement>) => void;
  handleClose: () => void;
  anchorEl: null | HTMLElement;
  forceStatusLabel?: boolean;
  splitTrigger?: boolean;
  pulseAttention?: boolean;
}

export const ActionButton = ({
  status,
  menuItems,
  handleClick,
  handleClose,
  anchorEl,
  forceStatusLabel = false,
  splitTrigger = false,
  pulseAttention = false,
}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'companyApplicants.applicantsCard.menuItems'});
  const {t: tr} = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const open = Boolean(anchorEl);

  const menuStyles = getMenuStyles(theme, true);
  const translatedStatus = t(`${snakeToCamel(status as MatchStatusTab)}`).toUpperCase();
  const statusLabel = translatedStatus.startsWith('companyApplicants.') ? status.toUpperCase() : translatedStatus;
  const buttonLabel =
    forceStatusLabel || status !== 'accepted' ? statusLabel : tr('companyApplicants.applicantsCard.menuItems.actions');
  let pulseSx = {};
  if (pulseAttention && !open) {
    pulseSx = {
      animation: 'statusPulse 1.4s ease-in-out',
      animationIterationCount: 3,
      '@keyframes statusPulse': {
        '0%': {
          boxShadow: `0 0 0 0 ${empionOrange[40]}`,
        },
        '70%': {
          boxShadow: '0 0 0 8px rgba(0, 0, 0, 0)',
        },
        '100%': {
          boxShadow: '0 0 0 0 rgba(0, 0, 0, 0)',
        },
      },
    };
  }

  return (
    <>
      <EmpionButton
        onClick={handleClick}
        sx={{
          backgroundColor: empionOrange[7],
          color: empionGreen[700],
          '&:hover': {color: empionGreen[700], backgroundColor: empionOrange[5]},
          px: splitTrigger ? 0 : undefined,
          overflow: splitTrigger ? 'hidden' : undefined,
          ...pulseSx,
        }}
      >
        {splitTrigger ? (
          <Box sx={{display: 'inline-flex', alignItems: 'stretch'}}>
            <Box sx={{px: 1.25, py: 0.5, display: 'flex', alignItems: 'center'}}>
              <Typography variant='body1' sx={{fontWeight: 600, lineHeight: 1}}>
                {buttonLabel}
              </Typography>
            </Box>
            <Box
              sx={{
                borderLeft: `1px solid ${empionOrange[3]}`,
                px: 0.75,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ArrowDropDownIcon />
            </Box>
          </Box>
        ) : (
          <>
            {buttonLabel}
            <ArrowDropDownIcon />
          </>
        )}
      </EmpionButton>

      <Menu
        id='basic-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        sx={menuStyles}
      >
        <EmpionActionMenuItems
          menuItems={menuItems || []}
          variant='body1'
          sx={{
            mr: '16px',
            ml: '26px',
            borderRadius: '10px',
            '&:hover': {
              backgroundColor: isDarkMode ? 'primary.dark' : 'background.menu',
              width: '100%',
            },
          }}
        />
      </Menu>
    </>
  );
};
